import type {ReactElement, ReactNode} from 'react';
import {useEffect} from 'react';
import React, {createContext, useContext, useState} from 'react';
import type {AppleDownloadProductsMap, AppleSubscriptionUpgradePrice, DownloadProductsData, Format, ProductsResponse} from '@Libraries/poster-download-library';
import {FREEBIE_PRODUCT_ID, fetchAppleDownloadProducts, fetchAppleSubscriptionUpgradePrice, getInitialDisplaySizeOption} from '@Libraries/poster-download-library';
import {getDurationForPoster} from '@Libraries/poster-download-library';
import {VideoDesignSizeOptionType} from '@Libraries/poster-download-library';
import {getRecommendedFileTypeFromPosterObject} from '@Libraries/poster-download-library';
import {isIOSApp} from '@Utils/browser.util';
import {isUserPremium} from '@Libraries/user.library';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import type {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {DownloadColorProfileOptionType} from '@Components/download-panel/components/download-color-profile-option/download-color-profile-option.types';
import {LetterSizeOptionType} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option.types';
import {useAppSelector} from '@/hooks';
import {DownloadBleedOptionTypes} from '@Components/download-panel/components/download-bleed-option/download-bleed-option.types';
import {doesPosterMeetGettyRequirements} from '@Libraries/poster-object.library';
import type {DownloadPageData, DownloadPanelDefaultOptions} from '@Components/download-panel/download-panel.types';
import type {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';

interface DownloadPanelContextData {
  productsData?: DownloadProductsData[];
  appleDownloadProducts?: AppleDownloadProductsMap;
  appleSubscriptionUpgradePrice?: AppleSubscriptionUpgradePrice;
  upgradeAmount?: number;
  upgradeDisplayAmount?: string;
  needsPurchase: boolean;
  wasPreviouslyPurchased: boolean;
  selectedProductId: number;
  posterObject: PosterLoadObject;
  availableFormats?: Record<string, Format[]>;
  setProductsData: (productsResponse: ProductsResponse) => void;
  setAppleDownloadProducts: (appleProducts: AppleDownloadProductsMap) => void;
  thumbnails?: Record<string, string>;

  scale: number;
  setSelectedProductId: (newVal: number) => void;
  setScale: (newVal: number) => void;
  transparency: boolean;
  setTransparency: (newVal: boolean) => void;
  margin: boolean;
  setMargin: (newVal: boolean) => void;
  colorProfile: DownloadColorProfileOptionType;
  setColorProfile: (newVal: DownloadColorProfileOptionType) => void;
  letterSize: LetterSizeOptionType;
  setLetterSize: (newVal: LetterSizeOptionType) => void;
  bleedUnit: DownloadBleedOptionTypes;
  setBleedUnit: (newVal: DownloadBleedOptionTypes) => void;
  bleed: number;
  setBleed: (newVal: number) => void;
  speed: number;
  setSpeed: (newVal: number) => void;
  duration: number;
  setDuration: (newVal: number) => void;
  fileType: FileTypeOptions;
  setFileType: (newVal: FileTypeOptions) => void;
  isWebDownload: boolean;
  videoScale: VideoDesignSizeOptionType;
  setVideoScale: (newVal: VideoDesignSizeOptionType) => void;
  videoSliderScale: number;
  setVideoSliderScale: (newVal: number) => void;
  originalDuration: number;
  isPDFWebQuality: boolean;
  setIsPDFWebQuality: (newVal: boolean) => void;
  meetsGettyRequirements: boolean;
  defaultOptions?: DownloadPanelDefaultOptions;
  posterVersionId?: string;
  downloadPageOptions?: DownloadPageData;
  showOutdatedPosterInfoBox?: boolean;
  selectedDisplaySizeOption: SizeDropdownOptions;
  setSelectedDisplaySizeOption: (newVal: SizeDropdownOptions) => void;
  selectedPages: string[];
  setSelectedPages: (newVal: string[]) => void;
}

const DownloadPanelContext = createContext<DownloadPanelContextData | null>(null);

export function useDownloadPanelData(): DownloadPanelContextData {
  const context = useContext(DownloadPanelContext);
  if (context === null) {
    throw new Error('useDownloadPanelData must be used within a ProductsDataProvider');
  }
  return context;
}

interface DownloadPanelProviderProps {
  children: ReactNode;
  productsResponse?: ProductsResponse;
  posterObject: PosterLoadObject;
  defaultOptions?: DownloadPanelDefaultOptions;
  posterVersionId?: string;
  downloadPageOptions?: DownloadPageData;
  showOutdatedPosterInfoBox?: boolean;
}

const DEFAULT_SCALE = 1;
const DEFAULT_BLEED = 0;
const DEFAULT_SPEED = 1;

export function DownloadPanelProvider({
  children,
  showOutdatedPosterInfoBox = false,
  posterVersionId = '',
  downloadPageOptions = undefined,
  ...props
}: DownloadPanelProviderProps): ReactElement {
  const [scale, setScale] = useState<number>(props.defaultOptions?.scale ?? DEFAULT_SCALE);
  const [bleed, setBleed] = useState<number>(props.defaultOptions?.bleed ?? DEFAULT_BLEED);
  const [bleedUnit, setBleedUnit] = useState<DownloadBleedOptionTypes>(props.defaultOptions?.bleedUnit ?? DownloadBleedOptionTypes.INCHES);
  const [colorProfile, setColorProfile] = useState<DownloadColorProfileOptionType>(DownloadColorProfileOptionType.RGB);
  const [transparency, setTransparency] = useState(props.defaultOptions?.transparency ?? false);
  const [margin, setMargin] = useState(props.defaultOptions?.margin ?? false);
  const [selectedProductId, setSelectedProductId] = useState<number>(FREEBIE_PRODUCT_ID);
  const [speed, setSpeed] = useState<number>(props.defaultOptions?.speed ?? DEFAULT_SPEED);
  const [letterSize, setLetterSize] = useState<LetterSizeOptionType>(props.defaultOptions?.letterSize ?? LetterSizeOptionType.NO);
  const [recommendedFileType, setRecommendedFileType] = useState<FileTypeOptions[]>(getRecommendedFileTypeFromPosterObject(props.posterObject));
  const [fileType, setFileType] = useState<FileTypeOptions>(props.defaultOptions?.fileType ?? getRecommendedFileTypeFromPosterObject(props.posterObject)[0]);
  const [videoScale, setVideoScale] = useState(VideoDesignSizeOptionType.FULL_HD);
  const posterTypeNames = useAppSelector((state) => {
    return state.downloadDialog.webDownloadPosterTypeNames;
  });
  const [posterObjectCopy, setPosterObjectCopy] = useState<PosterLoadObject>(props.posterObject);
  const [isWebDownload, setIsWebDownload] = useState(false);
  const [videoSliderScale, setVideoSliderScale] = useState(2);
  const [duration, setDuration] = useState<number>(getDurationForPoster(props.posterObject));
  const [originalDuration, setOriginalDuration] = useState<number>(getDurationForPoster(props.posterObject));
  const [isPDFWebQuality, setIsPDFWebQuality] = useState(props.defaultOptions?.isPDFWebQuality ?? true);
  const [meetsGettyRequirements, setMeetsGettyRequirements] = useState(true);
  const [selectedDisplaySizeOption, setSelectedDisplaySizeOption] = useState<SizeDropdownOptions>(
    getInitialDisplaySizeOption(props.defaultOptions?.scale, props.defaultOptions?.fileType ?? fileType, props.posterObject)
  );
  const [productsResponseState, setProductsResponseState] = useState<ProductsResponse | undefined>(props.productsResponse);
  const [appleDownloadProducts, setAppleDownloadProducts] = useState<AppleDownloadProductsMap | undefined>(undefined);
  const [appleSubscriptionUpgradePrice, setAppleSubscriptionUpgradePrice] = useState<AppleSubscriptionUpgradePrice | undefined>(undefined)

  useEffect((): void => {
    setProductsResponseState(props.productsResponse);
  }, [props.productsResponse]);

  useEffect(() => {
    if (productsResponseState?.productsData && isIOSApp() && !isUserPremium()) {
      if (!Object.keys(appleDownloadProducts ?? {}).length) {
        void fetchAppleDownloadProducts(productsResponseState.productsData).then((appleProducts) => {
          setAppleDownloadProducts(appleProducts);
        });
      }
      if (!appleSubscriptionUpgradePrice) {
        void fetchAppleSubscriptionUpgradePrice().then((subscriptionPrice) => {
          setAppleSubscriptionUpgradePrice(subscriptionPrice);
        });
      }
    }
  }, [productsResponseState]);

  const setProductsData = (productsResponse: ProductsResponse): void => {
    setProductsResponseState(productsResponse);
  };
  const [selectedPages, setSelectedPages] = useState<string[]>(() => {
    if (props.defaultOptions?.pagesHashedIds && props.defaultOptions?.pagesHashedIds.length > 0) {
      return props.defaultOptions?.pagesHashedIds;
    }
    return props.posterObject?.pages?.pageOrder ?? [];
  });

  useEffect(() => {
    setPosterObjectCopy(props.posterObject);
    setOriginalDuration(getDurationForPoster(props.posterObject));

    const gettyRequirements = doesPosterMeetGettyRequirements(props.posterObject);
    if (gettyRequirements !== meetsGettyRequirements) {
      setMeetsGettyRequirements(gettyRequirements);
    }

    const newRecommended = getRecommendedFileTypeFromPosterObject(props.posterObject);
    const arraysEqual = (a: FileTypeOptions[], b: FileTypeOptions[]): boolean => {
      if (a.length !== b.length) return false;
      return a.every((val, index) => val === b[index]);
    };
    if (!arraysEqual(newRecommended, recommendedFileType) && !props.defaultOptions?.fileType) {
      setFileType(newRecommended[0]);
      setRecommendedFileType(newRecommended);
    }
  }, [props.posterObject]);

  useEffect(() => {
    const pageOrder = props.posterObject?.pages?.pageOrder ?? [];
    if (pageOrder.length > 0) {
      setSelectedPages((currentPages) => {
        const pageIdSet = new Set(pageOrder);
        const validPages = currentPages.filter((pageId) => pageIdSet.has(pageId));

        if (validPages.length === 0) {
          return [...pageOrder];
        }

        if (validPages.length !== currentPages.length) {
          return validPages;
        }

        return currentPages;
      });
    }
  }, [props.posterObject?.pages?.pageOrder]);

  useEffect(() => {
    if (posterTypeNames && posterTypeNames.length > 0) {
      const isWeb = posterTypeNames.includes(props.posterObject.type.name);
      setIsWebDownload(isWeb);
    }
  }, [props.posterObject.type.name, posterTypeNames]);

  const value: DownloadPanelContextData = {
    productsData: productsResponseState?.productsData,
    appleDownloadProducts,
    appleSubscriptionUpgradePrice,
    upgradeAmount: productsResponseState?.upgradeAmount,
    upgradeDisplayAmount: productsResponseState?.upgradeDisplayAmount,
    needsPurchase: productsResponseState?.needsPurchase ?? false,
    wasPreviouslyPurchased: productsResponseState?.wasPreviouslyPurchased ?? false,
    thumbnails: productsResponseState?.thumbnails ?? undefined,
    selectedProductId,
    setSelectedProductId,
    setProductsData,
    setAppleDownloadProducts,
    posterObject: posterObjectCopy,
    scale,
    transparency,
    setTransparency,
    setScale,
    margin,
    setMargin,
    bleed,
    setBleed,
    colorProfile,
    setColorProfile,
    speed,
    setSpeed,
    letterSize,
    setLetterSize,
    fileType,
    setFileType,
    isWebDownload,
    bleedUnit,
    setBleedUnit,
    duration,
    setDuration,
    videoScale,
    setVideoScale,
    videoSliderScale,
    setVideoSliderScale,
    originalDuration,
    isPDFWebQuality,
    setIsPDFWebQuality,
    meetsGettyRequirements,
    defaultOptions: props.defaultOptions,
    posterVersionId,
    downloadPageOptions,
    showOutdatedPosterInfoBox,
    selectedDisplaySizeOption,
    setSelectedDisplaySizeOption,
    selectedPages,
    setSelectedPages,
  };

  return <DownloadPanelContext.Provider value={value}>{children}</DownloadPanelContext.Provider>;
}
